/**
 * Hooks del módulo de calidad (fichas de control, registros, NC, fotos).
 */

import { cliente } from '@/lib/cliente';
import { subirFoto } from '@/lib/fotos';
import type {
  ActualizarNoConformidad,
  AmbitoFicha,
  CrearRegistro,
  GuardarFicha,
  GuardarRespuestas,
  RespuestaApi,
  TipoFicha,
  VinculoFoto,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** Desenvuelve RespuestaApi<T> lanzando el error del backend. */
function datos<T>(r: RespuestaApi<T>): T {
  if (r.error !== null) throw new Error(r.error.mensaje);
  return r.data;
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

export function useCategoriasCalidad() {
  return useQuery({
    queryKey: ['calidad', 'categorias'],
    queryFn: async () => datos(await cliente.calidad.categorias()),
    staleTime: 10 * 60_000,
  });
}

export function useFichas(f: { tipo?: TipoFicha; ambito?: AmbitoFicha; activo?: boolean } = {}) {
  return useQuery({
    queryKey: ['calidad', 'fichas', f],
    queryFn: async () => datos(await cliente.calidad.fichas(f)),
  });
}

export function usePuntosCatalogo() {
  return useQuery({
    queryKey: ['calidad', 'puntos'],
    queryFn: async () => datos(await cliente.calidad.puntos()),
    staleTime: 60_000,
  });
}

export function useFicha(id: string | null | undefined) {
  return useQuery({
    queryKey: ['calidad', 'ficha', id],
    enabled: !!id,
    queryFn: async () => datos(await cliente.calidad.ficha(id as string)),
  });
}

export function useGuardarFicha() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string | null; body: GuardarFicha }) =>
      datos(
        id ? await cliente.calidad.guardarFicha(id, body) : await cliente.calidad.crearFicha(body),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calidad'] }),
  });
}

export function useEliminarFicha() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => datos(await cliente.calidad.eliminarFicha(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calidad'] }),
  });
}

// ---------------------------------------------------------------------------
// Registros
// ---------------------------------------------------------------------------

export function useControlesObra(codigo: string | undefined) {
  return useQuery({
    queryKey: ['calidad', 'controles', codigo],
    enabled: !!codigo,
    staleTime: 30_000,
    queryFn: async () => datos(await cliente.calidad.controlesObra(codigo as string)),
  });
}

export function useRegistros(f: { obra?: string; ficha?: string; estado?: string } = {}) {
  return useQuery({
    queryKey: ['calidad', 'registros', f],
    queryFn: async () => datos(await cliente.calidad.registros(f)),
  });
}

export function useRegistro(id: string | undefined) {
  return useQuery({
    queryKey: ['calidad', 'registro', id],
    enabled: !!id,
    queryFn: async () => datos(await cliente.calidad.registro(id as string)),
  });
}

export function useIniciarRegistro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrearRegistro) => datos(await cliente.calidad.iniciarRegistro(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calidad', 'controles'] }),
  });
}

/** Guarda respuestas (parcial). No refresca el registro: la pantalla mantiene el estado local. */
export function useGuardarRespuestas(id: string) {
  return useMutation({
    mutationFn: async (body: GuardarRespuestas) =>
      datos(await cliente.calidad.guardarRespuestas(id, body)),
  });
}

function invalidarRegistro(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['calidad', 'registro', id] });
  qc.invalidateQueries({ queryKey: ['calidad', 'controles'] });
  qc.invalidateQueries({ queryKey: ['calidad', 'registros'] });
  qc.invalidateQueries({ queryKey: ['calidad', 'nc'] });
}

export function useCerrarRegistro(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (nota?: string | null) =>
      datos(await cliente.calidad.cerrarRegistro(id, nota)),
    onSuccess: () => invalidarRegistro(qc, id),
  });
}

export function useLiberarRegistro(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (justificacion: string) =>
      datos(await cliente.calidad.liberarRegistro(id, justificacion)),
    onSuccess: () => invalidarRegistro(qc, id),
  });
}

export function useAnularRegistro(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (motivo: string) => datos(await cliente.calidad.anularRegistro(id, motivo)),
    onSuccess: () => invalidarRegistro(qc, id),
  });
}

// ---------------------------------------------------------------------------
// Fotos
// ---------------------------------------------------------------------------

export function useSubirFoto(registroId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ archivo, vinculo }: { archivo: File; vinculo: VinculoFoto }) =>
      subirFoto(archivo, vinculo),
    onSuccess: () => {
      if (registroId) qc.invalidateQueries({ queryKey: ['calidad', 'registro', registroId] });
      qc.invalidateQueries({ queryKey: ['calidad', 'nc'] });
    },
  });
}

export function useEliminarFoto(registroId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => datos(await cliente.calidad.eliminarFoto(id)),
    onSuccess: () => {
      if (registroId) qc.invalidateQueries({ queryKey: ['calidad', 'registro', registroId] });
    },
  });
}

// ---------------------------------------------------------------------------
// No conformidades
// ---------------------------------------------------------------------------

export function useNoConformidades(f: { obra?: string; estado?: string } = {}) {
  return useQuery({
    queryKey: ['calidad', 'nc', f],
    queryFn: async () => datos(await cliente.calidad.noConformidades(f)),
  });
}

export function useActualizarNoConformidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ActualizarNoConformidad }) =>
      datos(await cliente.calidad.actualizarNoConformidad(id, body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calidad', 'nc'] });
      qc.invalidateQueries({ queryKey: ['calidad', 'controles'] });
    },
  });
}
